// Keeps the screen from dimming or locking while something on it asks for that (issue #3608).
//
// The player asks while a song is loaded and the "Keep Screen Awake" song option is on: iPhone
// households saw songs replay over the next one, audio that could not be stopped and the
// sequencer's stopwatch warning after the phone locked mid-song (iOS suspends Web Audio when the
// screen locks, see issue #3525), and keeping the screen on sidesteps all of that until the deeper
// fix ships.
//
// The mechanism is the Screen Wake Lock API (navigator.wakeLock), which WebKit has implemented
// since iOS 16.4 by disabling the UIApplication idle timer of whatever app embeds the web view -
// Safari or the store app's WKWebView alike - and which Chrome and the Android WebView have had
// since version 84. The browser releases the lock on its own whenever the page is hidden (the app
// goes to the background, the phone is locked by hand), so the lock is requested again when the
// page becomes visible. Where the API is missing (desktop Safari before 16.4, Firefox, every test
// environment) nothing is held and the song option is hidden.
//
// A tiny ref-counted holder rather than a boolean: several components may want the screen awake
// at once, and each must be able to let go without releasing the others' hold.

export interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

export interface ScreenWakeLockDeps {
  // whether the Screen Wake Lock API exists; asked at every hold, never cached, so a test can
  // install the API after the module loaded
  isSupported: () => boolean;
  requestWakeLock: () => Promise<WakeLockSentinelLike>;
  isVisible: () => boolean;
  // notifies when the page's visibility changes; returns the unsubscribe
  subscribeVisibility: (listener: () => void) => () => void;
  log: (message: string) => void;
}

export class ScreenWakeLockController {
  private holders = 0;
  private sentinel: WakeLockSentinelLike | undefined;
  private requesting = false;
  private unsubscribeVisibility: (() => void) | undefined;

  constructor(private readonly deps: ScreenWakeLockDeps) {}

  isSupported(): boolean {
    return this.deps.isSupported();
  }

  isHeld(): boolean {
    return this.sentinel !== undefined;
  }

  // Asks for the screen to stay awake until the returned function is called. Calling it twice is
  // harmless. A no-op where the API is missing.
  hold(): () => void {
    if (!this.isSupported()) {
      return () => undefined;
    }
    this.holders++;
    if (this.holders === 1) {
      this.unsubscribeVisibility = this.deps.subscribeVisibility(() => this.reconcile());
    }
    this.reconcile();
    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      this.holders--;
      if (this.holders === 0) {
        this.unsubscribeVisibility?.();
        this.unsubscribeVisibility = undefined;
      }
      this.reconcile();
    };
  }

  private isWanted(): boolean {
    return this.holders > 0 && this.deps.isVisible();
  }

  // Brings the lock in line with what is wanted right now. A request already in flight is left to
  // finish; its resolution reconciles again, so a hold dropped meanwhile releases the lock then.
  private reconcile(): void {
    const wanted = this.isWanted();
    if (wanted && !this.sentinel && !this.requesting) {
      void this.request();
      return;
    }
    if (!wanted && this.sentinel) {
      const sentinel = this.sentinel;
      this.sentinel = undefined;
      sentinel.release().catch((error: unknown) => this.deps.log(`Screen wake lock release failed: ${error}`));
    }
  }

  private async request(): Promise<void> {
    this.requesting = true;
    let sentinel: WakeLockSentinelLike;
    try {
      sentinel = await this.deps.requestWakeLock();
    } catch (error) {
      // the browser may refuse for any reason (low battery, a permissions policy, the page hidden
      // by the time the request ran); there is nothing to do but wait for the next visibility or
      // hold change, which asks again
      this.requesting = false;
      this.deps.log(`Screen wake lock refused: ${error}`);
      return;
    }
    this.requesting = false;
    if (!this.isWanted()) {
      void sentinel.release();
      return;
    }
    this.sentinel = sentinel;
    // released by the browser (the page was hidden): forget it, so the next reconcile asks again
    sentinel.addEventListener('release', () => {
      if (this.sentinel === sentinel) {
        this.sentinel = undefined;
      }
    });
  }
}

function hasWakeLockApi(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator && navigator.wakeLock !== undefined;
}

export function isScreenWakeLockSupported(): boolean {
  return hasWakeLockApi();
}

const controller = new ScreenWakeLockController({
  isSupported: hasWakeLockApi,
  requestWakeLock: () => navigator.wakeLock.request('screen'),
  isVisible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
  subscribeVisibility: (listener) => {
    if (typeof document === 'undefined') {
      return () => undefined;
    }
    document.addEventListener('visibilitychange', listener);
    // the iOS webview coming back to the foreground is not always a visibilitychange (see
    // push_notifications.ts), but it is a pageshow
    window.addEventListener('pageshow', listener);
    return () => {
      document.removeEventListener('visibilitychange', listener);
      window.removeEventListener('pageshow', listener);
    };
  },
  log: (message) => console.log(message),
});

// The app-wide holder: keeps the screen awake until the returned function is called
export function holdScreenWakeLock(): () => void {
  return controller.hold();
}

export function isScreenWakeLockHeld(): boolean {
  return controller.isHeld();
}
